import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import {
  format, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  eachDayOfInterval, isToday, isSameMonth, addMonths, subMonths,
  isSameDay, startOfDay, addDays
} from 'date-fns'
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, Users, Building, Home, MapPin, X, AlertCircle, Move, Copy, Pencil, Trash2, ArrowRightLeft } from 'lucide-react'
import { apiClient } from '@/utils/api'
import { UnifiedAddEventModal } from './UnifiedAddEventModal'
import { useFamilyFeatures } from '@/hooks/useFamilyFeatures'
import { formatTimeRange, extractDateInTimezoneSync, parseDueDate } from '@/utils/timezoneUtils'
import { useAuth } from '@/hooks/useAuth'
import { useCustodyParents, custodySideOf } from '@/hooks/useCustodyParents'
import {
  useCalendarEvents,
  useDeleteFamilyEvent,
  useDeleteSchoolEvent,
  useDeleteAvailabilityOverride,
  useMeals,
  getMonthDateRange,
  UnifiedEvent,
  DayMealPlan,
  MealSlot,
} from '@/hooks/useCalendar'
import { UtensilsCrossed } from 'lucide-react'

// Ordered list of meal slots that actually have a chosen meal, for a day.
const MEAL_TYPE_ORDER: Array<'breakfast' | 'lunch' | 'dinner'> = ['breakfast', 'lunch', 'dinner']
const mealTypeLabel = (t: string): string => (t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Meal')
const presentMeals = (
  dm?: DayMealPlan,
): Array<{ type: 'breakfast' | 'lunch' | 'dinner'; slot: MealSlot }> => {
  if (!dm) return []
  const out: Array<{ type: 'breakfast' | 'lunch' | 'dinner'; slot: MealSlot }> = []
  for (const t of MEAL_TYPE_ORDER) {
    const slot = dm[t]
    if (slot && slot.meal_name) out.push({ type: t, slot })
  }
  return out
}

// Re-export for backward compatibility
export type { UnifiedEvent }

// Convert MDI icon strings to emoji
const getIconDisplay = (icon: string | null | undefined): string => {
  if (!icon) return '📋'
  if (icon.length <= 2 && !icon.startsWith('mdi:')) return icon

  const iconMap: Record<string, string> = {
    'mdi:check-circle': '✅',
    'mdi:broom': '🧹',
    'mdi:silverware-fork-knife': '🍽️',
    'mdi:bed': '🛏️',
    'mdi:shower': '🚿',
    'mdi:trash-can': '🗑️',
    'mdi:dog': '🐕',
    'mdi:cat': '🐈',
    'mdi:water': '💧',
    'mdi:food': '🍕',
    'mdi:book': '📖',
    'mdi:music': '🎵',
    'mdi:broom-clean': '🧹',
    'mdi:dishwasher': '🍽️',
    'mdi:tshirt-crew': '👕',
    'mdi:vacuum': '🧹',
    'mdi:paw': '🐾',
  }

  return iconMap[icon] || '📋'
}

interface Assignment {
  id: string
  chore: {
    name: string
    category: string
    icon?: string
  }
  assigned_to: string
  assigned_to_name?: string
  assigned_user?: {
    display_name: string
    username: string
    color_theme: string
  }
  status: string
  points_offered: number
  due_date: string
}

interface Child {
  id: string
  display_name: string
  username: string
  color_theme?: string
}

interface UnifiedCalendarDashboardProps {
  assignments?: Assignment[]
  children: Child[]
  onVerifyAssignment?: (assignmentId: string, approved: boolean) => void
  onDeleteAssignment?: (assignmentId: string) => void
  onReassignChore?: (assignmentId: string, newChildId: string) => void
  onEventCreated?: () => void
}

export function UnifiedCalendarDashboard({
  assignments = [],
  children,
  onVerifyAssignment,
  onDeleteAssignment,
  onReassignChore,
  onEventCreated
}: UnifiedCalendarDashboardProps) {
  const { isFeatureEnabled } = useFamilyFeatures()
  const { user } = useAuth()
  const custodyParents = useCustodyParents()
  const isKid = user && !user.is_parent
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [isAddEventModalOpen, setIsAddEventModalOpen] = useState(false)
  const [addEventInitialDate, setAddEventInitialDate] = useState<string>('')
  const [duplicateEvent, setDuplicateEvent] = useState<UnifiedEvent | null>(null)
  const [editEvent, setEditEvent] = useState<UnifiedEvent | null>(null)
  const [deleteConfirmEvent, setDeleteConfirmEvent] = useState<UnifiedEvent | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>('month')

  // Filter states
  const [showFamilyEvents, setShowFamilyEvents] = useState(true)
  const [showSchoolEvents, setShowSchoolEvents] = useState(true) // Controls both school and district
  const [showCustodyEvents, setShowCustodyEvents] = useState(true)
  const [selectedChildFilter, setSelectedChildFilter] = useState<string>('all')

  // Swipe gesture state
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null)
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null)

  // Drag and drop state
  const [draggedAssignment, setDraggedAssignment] = useState<Assignment | null>(null)
  const [dropTargetChild, setDropTargetChild] = useState<string | null>(null)
  const [reassignDropdownId, setReassignDropdownId] = useState<string | null>(null)
  const reassignDropdownRef = useRef<HTMLDivElement>(null)

  // Close reassign dropdown on click outside
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (reassignDropdownRef.current && !reassignDropdownRef.current.contains(e.target as Node)) {
      setReassignDropdownId(null)
    }
  }, [])

  useEffect(() => {
    if (reassignDropdownId) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [reassignDropdownId, handleClickOutside])

  // Use React Query for calendar events - automatic cache invalidation on mutations
  const custodyEnabled = isFeatureEnabled('custody_schedule')
  const dateRange = useMemo(() => getMonthDateRange(currentDate), [currentDate])
  const { data: events = [], isLoading: loading } = useCalendarEvents(dateRange, { showCustodySchedule: custodyEnabled })
  const { data: mealsData = new Map<string, DayMealPlan>() } = useMeals(dateRange)

  // Delete mutations with automatic cache invalidation
  const deleteFamilyMutation = useDeleteFamilyEvent()
  const deleteSchoolMutation = useDeleteSchoolEvent()
  const deleteAvailabilityMutation = useDeleteAvailabilityOverride()

  const getEventsForDate = (date: Date): UnifiedEvent[] => {
    const dateStr = format(date, 'yyyy-MM-dd')

    // First check how many total events we have
    const totalEvents = events.length
    const eventsForThisDate = events.filter(evt => {
      const eventDate = evt.date ? extractDateInTimezoneSync(evt.date) : ''
      return eventDate === dateStr
    })

    // Debug logging removed for production

    const filtered = events.filter(evt => {
      // Normalize event date using timezone-aware extraction
      const eventDate = evt.date ? extractDateInTimezoneSync(evt.date) : ''
      if (eventDate !== dateStr) return false
      if (!showFamilyEvents && evt.type === 'family') return false
      if (!showSchoolEvents && (evt.type === 'school' || evt.type === 'district')) return false
      if (!showCustodyEvents && evt.type === 'custody') return false

      // Filter custody events by selected child
      if (evt.type === 'custody' && selectedChildFilter !== 'all') {
        const selectedChild = children.find(c => c.id === selectedChildFilter)
        if (selectedChild && evt.child !== selectedChild.display_name) {
          return false
        }
      }

      return true
    })

    return filtered
  }

  const getAssignmentsForDate = (date: Date): Assignment[] => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return assignments.filter(a => {
      if (!a.due_date) return false
      const parsedDate = parseDueDate(a.due_date)
      if (isNaN(parsedDate.getTime())) return false
      const assignmentDate = format(startOfDay(parsedDate), 'yyyy-MM-dd')
      if (assignmentDate !== dateStr) return false
      if (selectedChildFilter !== 'all' && a.assigned_to !== selectedChildFilter) return false
      return true
    })
  }

  const getMealsForDate = (date: Date): DayMealPlan | undefined => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return mealsData.get(dateStr)
  }

  const getEventTypeColor = (type: string): string => {
    switch (type) {
      case 'family': return 'bg-blue-500'
      case 'school': return 'bg-green-500'
      case 'district': return 'bg-purple-500'
      case 'custody': return 'bg-pink-500'
      case 'availability': return 'bg-orange-500'
      default: return 'bg-gray-500'
    }
  }

  const getEventTypeLabel = (type: string): string => {
    switch (type) {
      case 'family': return 'Family'
      case 'school': return 'School'
      case 'district': return 'District'
      case 'custody': return 'Custody'
      case 'availability': return 'availability'
      default: return type
    }
  }

  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case 'family': return Users
      case 'school': return CalendarIcon
      case 'district': return Building
      case 'custody': return Home
      case 'availability': return MapPin
      default: return CalendarIcon
    }
  }

  // Get emoji for specific event subtypes (family event_type, school event_type, etc.)
  const getEventEmoji = (evt: any): string => {
    const eventType = evt.event_type || ''
    const broadType = evt.type || ''

    // Family event types
    const familyEmojis: Record<string, string> = {
      'custody': '🏠',
      'school_activity': '🎓',
      'sports': '⚽',
      'work': '💼',
      'appointment': '🏥',
      'celebration': '🎉',
      'religious': '⛪',
      'travel': '✈️',
      'fun_activity': '🎮',
      'birthday': '🎂',
      'other': '📅',
    }

    // School event types
    const schoolEmojis: Record<string, string> = {
      'school_event': '🏫',
      'sports': '🏆',
      'club': '🎯',
      'performance': '🎭',
      'other': '📚',
    }

    // District event types
    const districtEmojis: Record<string, string> = {
      'holiday': '🎊',
      'break': '🌴',
      'early_release': '⏰',
      'teacher_workday': '👩‍🏫',
      'testing': '📝',
      'other': '🏛️',
    }

    // Availability override types
    const availabilityEmojis: Record<string, string> = {
      'at_parent1': '🏠',
      'at_parent2': '🏡',
      'work': '💼',
      'friend': '👫',
      'event': '🎪',
      'school': '🏫',
      'other': '📍',
    }

    if (broadType === 'family') return familyEmojis[eventType] || '👨‍👩‍👧‍👦'
    if (broadType === 'school') return schoolEmojis[eventType] || '🏫'
    if (broadType === 'district') return districtEmojis[eventType] || '🏛️'
    if (broadType === 'custody') return '🏠'
    if (broadType === 'availability') return availabilityEmojis[eventType] || '📍'
    return '📅'
  }

  const getCustodyDataForDate = (date: Date): Map<string, boolean> => {
    const dateStr = format(date, 'yyyy-MM-dd')
    const custodyMap = new Map<string, boolean>()

    // Check if this family uses custody tracking at all
    const hasCustodyEvents = events.some(evt => evt.type === 'custody')
    if (!hasCustodyEvents || custodyParents.loading) {
      // No custody tracking - all children are present by default
      children.forEach(child => custodyMap.set(child.display_name, true))
      return custodyMap
    }

    // Get custody events for this date where child is with current parent
    const custodyEvents = events.filter(evt => {
      const eventDate = evt.date ? extractDateInTimezoneSync(evt.date) : ''
      return eventDate === dateStr && evt.type === 'custody'
    })

    // Default all children to home as safety net
    children.forEach(child => custodyMap.set(child.display_name, true))

    // Override with actual custody data where available. "Present" means with
    // the household parent (the family's first custody parent), whichever
    // parent is signed in.
    custodyEvents.forEach(evt => {
      if (evt.child) {
        custodyMap.set(evt.child, custodySideOf(evt.custody_parent, custodyParents) === 'primary')
      }
    })

    return custodyMap
  }

  const navigatePrevious = () => {
    if (viewMode === 'day') {
      setCurrentDate(addDays(currentDate, -1))
    } else if (viewMode === 'week') {
      setCurrentDate(addDays(currentDate, -7))
    } else {
      setCurrentDate(subMonths(currentDate, 1))
    }
  }

  const navigateNext = () => {
    if (viewMode === 'day') {
      setCurrentDate(addDays(currentDate, 1))
    } else if (viewMode === 'week') {
      setCurrentDate(addDays(currentDate, 7))
    } else {
      setCurrentDate(addMonths(currentDate, 1))
    }
  }

  const navigateToday = () => {
    setCurrentDate(new Date())
  }

  // Get week days for week view
  const getWeekDays = (): Date[] => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 })
    return eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) })
  }

  // Get header text based on view mode
  const getHeaderText = (): string => {
    if (viewMode === 'day') {
      return format(currentDate, 'EEEE, MMMM d, yyyy')
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 })
      const weekEnd = addDays(weekStart, 6)
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`
    }
    return format(currentDate, 'MMMM yyyy')
  }

  const handleDayClick = (date: Date) => {
    // On mobile (< 768px), clicking a day in month/week view switches to day view
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
    if (isMobile && viewMode !== 'day') {
      setCurrentDate(date)
      setViewMode('day')
    } else {
      setSelectedDate(isSameDay(date, selectedDate || new Date('1900-01-01')) ? null : date)
    }
  }

  const handleAddEvent = (date?: Date) => {
    const dateToUse = date || new Date()
    setAddEventInitialDate(format(dateToUse, 'yyyy-MM-dd'))
    setIsAddEventModalOpen(true)
  }

  const handleEventCreated = () => {
    setIsAddEventModalOpen(false)
    setEditEvent(null)
    // No need to call loadEvents - React Query cache automatically invalidates
    if (onEventCreated) {
      onEventCreated()
    }
  }

  const handleDeleteEvent = async (event: UnifiedEvent) => {
    setIsDeleting(true)
    try {
      // Use different mutations based on event type - cache automatically invalidates
      if (event.type === 'family') {
        await deleteFamilyMutation.mutateAsync(event.id)
      } else if (event.type === 'school' || event.type === 'district') {
        await deleteSchoolMutation.mutateAsync(event.id)
      } else if (event.type === 'availability') {
        await deleteAvailabilityMutation.mutateAsync(event.id)
      }
      setDeleteConfirmEvent(null)
      // No need to call loadEvents - cache automatically invalidated by mutations
    } catch (error) {
      console.error('Failed to delete event:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const canEditEvent = (event: UnifiedEvent): boolean => {
    // Parents can edit family, school, district, and availability events
    if (!isKid) {
      return event.type === 'family' || event.type === 'school' || event.type === 'district' || event.type === 'availability'
    }
    // Kids can only edit their own family events
    if (event.type === 'family') {
      return event.created_by === user?.id
    }
    // Kids can only edit their own availability overrides
    if (event.type === 'availability') {
      return event.user_id === user?.id
    }
    return false
  }

  // Swipe gesture handlers
  const minSwipeDistance = 50

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    })
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    })
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distanceX = touchStart.x - touchEnd.x
    const distanceY = touchStart.y - touchEnd.y
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY)

    if (isHorizontalSwipe && Math.abs(distanceX) > minSwipeDistance) {
      if (distanceX > 0) {
        navigateNext()
      } else {
        navigatePrevious()
      }
    }
  }

  // Drag and drop handlers
  const handleDragStart = (assignment: Assignment) => (e: React.DragEvent) => {
    setDraggedAssignment(assignment)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', assignment.id)
  }

  const handleDragEnd = () => {
    setDraggedAssignment(null)
    setDropTargetChild(null)
  }

  const handleDragOver = (childId: string) => (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'
    setDropTargetChild(childId)
  }

  const handleDragEnter = (childId: string) => (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDropTargetChild(childId)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.stopPropagation()
    const relatedTarget = e.relatedTarget as Node | null
    const currentTarget = e.currentTarget as Node
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      setDropTargetChild(null)
    }
  }

  const handleDrop = (childId: string) => (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (draggedAssignment && draggedAssignment.assigned_to !== childId && onReassignChore) {
      onReassignChore(draggedAssignment.id, childId)
    }
    setDraggedAssignment(null)
    setDropTargetChild(null)
  }

  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 })
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd })

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-semibold">{getHeaderText()}</h2>
          <div className="flex items-center justify-between md:justify-end gap-2 flex-wrap">
            {/* View Mode Selector - More prominent on mobile */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('day')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'day' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Day
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'week' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Week
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'month' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Month
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={navigateToday}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Today
              </button>
              <button
                onClick={navigatePrevious}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={navigateNext}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleAddEvent(selectedDate || undefined)}
                className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Event</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-700">Show:</span>
            <label className="flex items-center space-x-1 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showFamilyEvents}
                onChange={(e) => setShowFamilyEvents(e.target.checked)}
                className="rounded"
              />
              <span className={`inline-block w-3 h-3 rounded ${getEventTypeColor('family')}`}></span>
              <span>Family</span>
            </label>
            <label className="flex items-center space-x-1 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showSchoolEvents}
                onChange={(e) => setShowSchoolEvents(e.target.checked)}
                className="rounded"
              />
              <span className={`inline-block w-3 h-3 rounded ${getEventTypeColor('school')}`}></span>
              <span>School</span>
            </label>
            {/* Only show Custody filter if custody events exist */}
            {events.some(evt => evt.type === 'custody') && (
              <label className="flex items-center space-x-1 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showCustodyEvents}
                  onChange={(e) => setShowCustodyEvents(e.target.checked)}
                  className="rounded"
                />
                <span className={`inline-block w-3 h-3 rounded ${getEventTypeColor('custody')}`}></span>
                <span>Custody</span>
              </label>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Filter by child:</label>
            <select
              value={selectedChildFilter}
              onChange={(e) => setSelectedChildFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value="all">All Children</option>
              {children.map(child => (
                <option key={child.id} value={child.id}>
                  {child.display_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Day View */}
      {viewMode === 'day' && (
        <div
          className="bg-white rounded-lg shadow-sm p-4 flex-1"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {(() => {
            const dayEvents = getEventsForDate(currentDate).filter(evt => evt.type !== 'custody')
            const dayAssignments = getAssignmentsForDate(currentDate)
            const custodyData = getCustodyDataForDate(currentDate)
            const dayMeals = getMealsForDate(currentDate)
            const isTodays = isToday(currentDate)

            return (
              <div className="space-y-4">
                {/* Today indicator */}
                {isTodays && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-700 font-medium">
                    Today
                  </div>
                )}

                {/* Meals Section - Show prominently at top */}
                {presentMeals(dayMeals).map(({ type, slot }) => (
                  <div key={type} className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-orange-500 rounded-lg">
                        <UtensilsCrossed className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-orange-700 uppercase tracking-wide">
                          {isTodays && type === 'dinner' ? "Tonight's Dinner" : mealTypeLabel(type)}
                        </h4>
                        <p className="text-lg font-semibold text-gray-900">{slot.meal_name}</p>
                        {slot.cook_name && (
                          <p className="text-sm text-gray-600 mt-1">Chef: {slot.cook_name}</p>
                        )}
                        {slot.notes && (
                          <p className="text-sm text-gray-500 mt-1">{slot.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Events Section */}
                {dayEvents.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Events</h4>
                    <div className="space-y-2">
                      {dayEvents.map((evt) => {
                        return (
                          <div
                            key={evt.id}
                            className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50"
                          >
                            <div className={`flex items-start gap-3 ${evt.is_cancelled ? 'opacity-60' : ''}`}>
                              <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${getEventTypeColor(evt.type)} bg-opacity-10 text-lg`}>
                                {getEventEmoji(evt)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <h5 className={`font-medium ${evt.is_cancelled ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{evt.title}</h5>
                                  <div className="flex items-center gap-1">
                                    {canEditEvent(evt) && (
                                      <>
                                        <button
                                          onClick={() => {
                                            setEditEvent(evt)
                                            setAddEventInitialDate(evt.date)
                                            setIsAddEventModalOpen(true)
                                          }}
                                          className="p-1.5 hover:bg-blue-100 rounded-md transition-colors"
                                          title="Edit event"
                                        >
                                          <Pencil className="w-3.5 h-3.5 text-blue-500" />
                                        </button>
                                        <button
                                          onClick={() => setDeleteConfirmEvent(evt)}
                                          className="p-1.5 hover:bg-red-100 rounded-md transition-colors"
                                          title="Delete event"
                                        >
                                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                        </button>
                                      </>
                                    )}
                                    <button
                                      onClick={() => {
                                        setDuplicateEvent(evt)
                                        setAddEventInitialDate(format(currentDate, 'yyyy-MM-dd'))
                                        setIsAddEventModalOpen(true)
                                      }}
                                      className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                                      title="Duplicate event"
                                    >
                                      <Copy className="w-3.5 h-3.5 text-gray-500" />
                                    </button>
                                  </div>
                                </div>
                                {evt.description && (
                                  <p className="text-sm text-gray-600 mt-1">{evt.description}</p>
                                )}
                                <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-500">
                                  {evt.all_day ? (
                                    <span>All day</span>
                                  ) : (
                                    <span>{formatTimeRange(evt.start_time, evt.end_time)}</span>
                                  )}
                                  {evt.location && (() => {
                                    const parts = evt.location.split(' | ')
                                    const displayName = parts.length >= 2 ? parts[0] : evt.location
                                    const address = parts.length >= 2 ? parts.slice(1).join(' | ') : null
                                    const mapsQuery = address || evt.location
                                    return (
                                      <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                                        onClick={e => e.stopPropagation()}
                                      >
                                        <MapPin className="w-3 h-3 flex-shrink-0" />
                                        {displayName}
                                      </a>
                                    )
                                  })()}
                                  {evt.school && (
                                    <span className="flex items-center gap-1">
                                      <Building className="w-3 h-3 flex-shrink-0" />
                                      {evt.school}
                                    </span>
                                  )}
                                </div>
                                {evt.attendees && evt.attendees.length > 0 && (() => {
                                  const names = evt.attendees
                                    .map(id => children.find(c => c.id === id)?.display_name)
                                    .filter(Boolean)
                                  return names.length > 0 ? (
                                    <div className="flex items-center gap-1 mt-1 text-xs text-purple-600">
                                      <Users className="w-3 h-3 flex-shrink-0" />
                                      {names.join(', ')}
                                    </div>
                                  ) : null
                                })()}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Assignments Section */}
                {dayAssignments.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Chores</h4>
                    <div className="space-y-2">
                      {dayAssignments.map((assignment) => (
                        <div
                          key={assignment.id}
                          className="border border-gray-200 rounded-lg p-3"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{getIconDisplay(assignment.chore.icon)}</span>
                              <div>
                                <h6 className="font-medium text-gray-900">{assignment.chore.name}</h6>
                                <p className="text-xs text-gray-500">
                                  {assignment.assigned_user?.display_name || assignment.assigned_to_name}
                                </p>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded font-medium ${
                              assignment.status === 'verified' ? 'bg-green-100 text-green-700' :
                              assignment.status === 'pending_verification' ? 'bg-yellow-100 text-yellow-700' :
                              assignment.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {assignment.status === 'pending_verification' ? 'pending' : assignment.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Children Status */}
                {children.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Children Status</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {children.map(child => {
                        const isPresent = custodyData.get(child.display_name) || false
                        const CustodyIcon = isPresent ? Home : MapPin
                        return (
                          <div
                            key={child.id}
                            className={`p-3 rounded-lg border ${
                              isPresent ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <CustodyIcon className={`w-4 h-4 ${isPresent ? 'text-blue-600' : 'text-gray-400'}`} />
                              <span className={`font-medium ${isPresent ? 'text-blue-900' : 'text-gray-600'}`}>
                                {child.display_name}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{isPresent ? 'At home' : 'Away'}</p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {dayEvents.length === 0 && dayAssignments.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No events or chores for this day</p>
                  </div>
                )}

                {/* Add Event Button - always visible */}
                <div className={`text-center ${dayEvents.length === 0 && dayAssignments.length === 0 ? '' : 'mt-4 pt-3 border-t border-gray-100'}`}>
                  <button
                    onClick={() => handleAddEvent(currentDate)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    + Add an event
                  </button>
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {/* Week View */}
      {viewMode === 'week' && (
        <div
          className="bg-white rounded-lg shadow-sm p-4 flex-1"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="space-y-2">
            {getWeekDays().map(date => {
              const dateEvents = getEventsForDate(date).filter(evt => evt.type !== 'custody')
              const dateAssignments = getAssignmentsForDate(date)
              const custodyData = getCustodyDataForDate(date)
              const dateMeals = getMealsForDate(date)
              const isTodays = isToday(date)

              return (
                <div
                  key={date.toString()}
                  onClick={() => {
                    setCurrentDate(date)
                    setViewMode('day')
                  }}
                  className={`p-3 border rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${
                    isTodays ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`text-center min-w-12 ${isTodays ? 'text-blue-600' : 'text-gray-900'}`}>
                        <div className="text-xs text-gray-500">{format(date, 'EEE')}</div>
                        <div className="text-xl font-bold">{format(date, 'd')}</div>
                      </div>
                      {isTodays && (
                        <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">Today</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Custody indicators */}
                      {children.slice(0, 3).map(child => {
                        const isPresent = custodyData.get(child.display_name) || false
                        if (!isPresent) return null
                        return (
                          <div
                            key={child.id}
                            className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center"
                            title={`${child.display_name} - Home`}
                          >
                            <span className="text-xs font-medium text-blue-700">
                              {child.display_name.charAt(0)}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Events preview */}
                  {(dateEvents.length > 0 || dateAssignments.length > 0 || presentMeals(dateMeals).length > 0) && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {/* Meal indicators */}
                      {presentMeals(dateMeals).map(({ type, slot }) => (
                        <div key={type} className="text-xs px-2 py-1 rounded flex items-center gap-1 bg-orange-500 text-white" title={`${mealTypeLabel(type)}: ${slot.meal_name}`}>
                          <UtensilsCrossed className="w-3 h-3" />
                          <span className="truncate max-w-24">{slot.meal_name}</span>
                        </div>
                      ))}
                      {dateEvents.map((evt) => (
                          <div
                            key={evt.id}
                            className={`text-xs px-2 py-1 rounded flex items-center gap-1 text-white ${getEventTypeColor(evt.type)}`}
                          >
                            <span className="text-sm leading-none">{getEventEmoji(evt)}</span>
                            <span className={`truncate max-w-24 ${evt.is_cancelled ? 'line-through opacity-60' : ''}`}>{evt.title}</span>
                          </div>
                      ))}
                      {dateAssignments.length > 0 && (
                        <div className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">
                          {dateAssignments.length} chore{dateAssignments.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Month View (existing) */}
      {viewMode === 'month' && (
        <div
          className="bg-white rounded-lg shadow-sm p-4 flex-1"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-xs font-medium text-gray-600 text-center py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1">
            {days.map(date => {
              const dateEvents = getEventsForDate(date).filter(evt => evt.type !== 'custody')
              const dateAssignments = getAssignmentsForDate(date)
              const custodyData = getCustodyDataForDate(date)
              const dateMeals = getMealsForDate(date)
              const isCurrentMonth = isSameMonth(date, currentDate)
              const isTodays = isToday(date)
              const isSelected = selectedDate && isSameDay(date, selectedDate)

              return (
                <div
                  key={date.toString()}
                  onClick={() => handleDayClick(date)}
                  onDoubleClick={() => handleAddEvent(date)}
                  className={`min-h-32 lg:min-h-44 p-2 border rounded-lg cursor-pointer transition-all ${
                    isTodays ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  } ${
                    isSelected ? 'ring-2 ring-blue-400' : ''
                  } ${
                    !isCurrentMonth ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`text-sm font-medium mb-2 ${
                    isTodays ? 'text-blue-600' : !isCurrentMonth ? 'text-gray-400' : 'text-gray-900'
                  }`}>
                    {format(date, 'd')}
                  </div>

                  <div className="space-y-1">
                    {/* Meal indicators */}
                    {presentMeals(dateMeals).map(({ type, slot }) => (
                      <div
                        key={type}
                        className="text-xs px-1.5 py-0.5 rounded flex items-center gap-1 bg-orange-500 text-white"
                        title={`${mealTypeLabel(type)}: ${slot.meal_name}`}
                      >
                        <UtensilsCrossed className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate font-medium">{slot.meal_name}</span>
                      </div>
                    ))}

                    {/* Event indicators with icons */}
                    {dateEvents.map((evt) => {
                      return (
                        <div
                          key={evt.id}
                          className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-1 text-white ${getEventTypeColor(evt.type)}`}
                          title={`${getEventTypeLabel(evt.type)}: ${evt.title}`}
                        >
                          <span className="text-sm leading-none flex-shrink-0">{getEventEmoji(evt)}</span>
                          <span className={`truncate font-medium ${evt.is_cancelled ? 'line-through opacity-60' : ''}`}>{evt.title}</span>
                        </div>
                      )
                    })}

                    {/* Child custody indicators */}
                    {children.map(child => {
                      const isPresent = custodyData.get(child.display_name) || false
                      if (!isPresent && selectedChildFilter === 'all') return null
                      if (selectedChildFilter !== 'all' && child.id !== selectedChildFilter) return null

                      const childAssignments = dateAssignments.filter(a => a.assigned_to === child.id)
                      const completedCount = childAssignments.filter(a =>
                        a.status === 'completed' || a.status === 'verified' || a.status === 'pending_verification'
                      ).length

                      const CustodyIcon = isPresent ? Home : MapPin

                      return (
                        <div
                          key={child.id}
                          className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-1 ${
                            isPresent ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'
                          }`}
                          title={`${child.display_name}${isPresent ? ' - Home' : ' - Away'}`}
                        >
                          <CustodyIcon className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate font-medium text-xs flex-1">{child.display_name}</span>
                          {childAssignments.length > 0 && (
                            <span className="font-bold text-xs">
                              {completedCount}/{childAssignments.length}
                            </span>
                          )}
                        </div>
                      )
                    })}

                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Detail Panel for Selected Date (only in month view) */}
      {selectedDate && viewMode === 'month' && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </h3>
            <button
              onClick={() => setSelectedDate(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Events for Selected Date */}
          {(() => {
            const dayEvents = getEventsForDate(selectedDate).filter(evt => evt.type !== 'custody')
            const dayAssignments = getAssignmentsForDate(selectedDate)
            const custodyData = getCustodyDataForDate(selectedDate)
            const dayMeals = getMealsForDate(selectedDate)

            return (
              <div className="space-y-4">
                {/* Meals Section */}
                {presentMeals(dayMeals).map(({ type, slot }) => (
                  <div key={type} className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-500 rounded-lg">
                        <UtensilsCrossed className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-orange-700">{mealTypeLabel(type)}</h4>
                        <p className="text-base font-semibold text-gray-900">{slot.meal_name}</p>
                        {slot.cook_name && (
                          <p className="text-sm text-gray-600 mt-1">Chef: {slot.cook_name}</p>
                        )}
                        {slot.notes && (
                          <p className="text-sm text-gray-500 mt-1">{slot.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Events Section */}
                {dayEvents.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Events</h4>
                    <div className="space-y-2">
                      {dayEvents.map((evt) => {
                        return (
                          <div
                            key={evt.id}
                            className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50"
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${getEventTypeColor(evt.type)} bg-opacity-10 text-lg`}>
                                {getEventEmoji(evt)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <div className="flex items-center gap-2">
                                    <h5 className="font-medium text-gray-900">{evt.title}</h5>
                                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                                      {getEventTypeLabel(evt.type)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {canEditEvent(evt) && (
                                      <>
                                        <button
                                          onClick={() => {
                                            setEditEvent(evt)
                                            setAddEventInitialDate(evt.date)
                                            setIsAddEventModalOpen(true)
                                          }}
                                          className="p-1.5 hover:bg-blue-100 rounded-md transition-colors"
                                          title="Edit event"
                                        >
                                          <Pencil className="w-3.5 h-3.5 text-blue-500" />
                                        </button>
                                        <button
                                          onClick={() => setDeleteConfirmEvent(evt)}
                                          className="p-1.5 hover:bg-red-100 rounded-md transition-colors"
                                          title="Delete event"
                                        >
                                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                        </button>
                                      </>
                                    )}
                                    <button
                                      onClick={() => {
                                        setDuplicateEvent(evt)
                                        setAddEventInitialDate(selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '')
                                        setIsAddEventModalOpen(true)
                                      }}
                                      className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                                      title="Duplicate event"
                                    >
                                      <Copy className="w-3.5 h-3.5 text-gray-500" />
                                    </button>
                                  </div>
                                </div>
                                {evt.description && (
                                  <p className="text-sm text-gray-600 mb-2">{evt.description}</p>
                                )}
                                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                  {evt.all_day ? (
                                    <span>All day</span>
                                  ) : (
                                    <span>{formatTimeRange(evt.start_time, evt.end_time)}</span>
                                  )}
                                  {evt.location && (() => {
                                    const parts = evt.location.split(' | ')
                                    const displayName = parts.length >= 2 ? parts[0] : evt.location
                                    const address = parts.length >= 2 ? parts.slice(1).join(' | ') : null
                                    const mapsQuery = address || evt.location
                                    return (
                                      <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                                        onClick={e => e.stopPropagation()}
                                      >
                                        <MapPin className="w-3 h-3 flex-shrink-0" />
                                        {displayName}
                                      </a>
                                    )
                                  })()}
                                  {evt.school && (
                                    <span className="flex items-center gap-1">
                                      <Building className="w-3 h-3" />
                                      {evt.school}
                                    </span>
                                  )}
                                  {evt.district && (
                                    <span className="flex items-center gap-1">
                                      <Building className="w-3 h-3" />
                                      {evt.district}
                                    </span>
                                  )}
                                </div>
                                {/* Event flags */}
                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                  {evt.is_cancelled && (
                                    <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 font-medium">
                                      CANCELLED
                                    </span>
                                  )}
                                  {evt.game_type && (
                                    <span className={`text-xs px-2 py-1 rounded font-medium flex items-center gap-1 ${
                                      evt.game_type === 'home'
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-orange-100 text-orange-700'
                                    }`}>
                                      {evt.game_type === 'home' ? <Home className="w-3 h-3" /> : '🚌'}
                                      {evt.game_type === 'home' ? 'Home' : 'Away'}
                                    </span>
                                  )}
                                  {evt.early_release && (
                                    <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 font-medium">
                                      Early Release
                                    </span>
                                  )}
                                  {evt.affects_chores && (
                                    <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-800 border border-orange-300 font-medium flex items-center gap-1">
                                      <AlertCircle className="w-3 h-3" />
                                      Affects Chores
                                    </span>
                                  )}
                                  {evt.needs_ride && (
                                    <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 font-medium">
                                      Needs Ride
                                    </span>
                                  )}
                                </div>
                                {evt.attendees && evt.attendees.length > 0 && (() => {
                                  const names = evt.attendees
                                    .map(id => children.find(c => c.id === id)?.display_name)
                                    .filter(Boolean)
                                  return names.length > 0 ? (
                                    <div className="flex items-center gap-1 mt-1 text-xs text-purple-600">
                                      <Users className="w-3 h-3 flex-shrink-0" />
                                      {names.join(', ')}
                                    </div>
                                  ) : null
                                })()}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Assignments Section - Grouped by Child with Drag and Drop */}
                {(dayAssignments.length > 0 || draggedAssignment) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Chores by Child</h4>
                    <div className="space-y-3">
                      {children.map(child => {
                        const childAssignments = dayAssignments.filter(a => a.assigned_to === child.id)
                        const isDropTarget = dropTargetChild === child.id
                        const isBeingDraggedFrom = draggedAssignment?.assigned_to === child.id

                        return (
                          <div
                            key={child.id}
                            className={`p-3 border-2 rounded-lg transition-all ${
                              isDropTarget ? 'bg-blue-50 border-blue-400 border-dashed' :
                              isBeingDraggedFrom ? 'opacity-60 border-gray-200' : 'border-gray-200'
                            }`}
                            onDragOver={handleDragOver(child.id)}
                            onDragEnter={handleDragEnter(child.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop(child.id)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-semibold text-gray-900 flex items-center gap-2">
                                {child.display_name}
                                {isDropTarget && (
                                  <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded animate-pulse">
                                    Drop here
                                  </span>
                                )}
                              </h5>
                              <span className="text-xs text-gray-500">
                                {childAssignments.filter(a => a.status === 'completed' || a.status === 'verified' || a.status === 'pending_verification').length}/{childAssignments.length}
                              </span>
                            </div>

                            {childAssignments.length === 0 ? (
                              <p className="text-xs text-gray-400 italic">No chores today</p>
                            ) : (
                            <div className="space-y-2">
                              {childAssignments.map((assignment) => {
                                const isDragging = draggedAssignment?.id === assignment.id
                                const isDraggable = !isKid && assignment.status === 'pending'

                                return (
                                  <div
                                    key={assignment.id}
                                    draggable={isDraggable}
                                    onDragStart={isDraggable ? handleDragStart(assignment) : undefined}
                                    onDragEnd={isDraggable ? handleDragEnd : undefined}
                                    className={`border border-gray-200 rounded-lg p-2 hover:bg-gray-50 ${
                                      isDraggable ? 'cursor-move' : ''
                                    } ${isDragging ? 'opacity-40' : ''} transition-opacity`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        {isDraggable && <Move className="w-3 h-3 text-gray-400 flex-shrink-0" />}
                                        <span className="text-base">{getIconDisplay(assignment.chore.icon)}</span>
                                        <div className="flex-1 min-w-0">
                                          <h6 className="font-medium text-gray-900 text-sm truncate">{assignment.chore.name}</h6>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className={`text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap ${
                                          assignment.status === 'verified' ? 'bg-green-100 text-green-700' :
                                          assignment.status === 'pending_verification' ? 'bg-yellow-100 text-yellow-700' :
                                          assignment.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                                          'bg-gray-100 text-gray-700'
                                        }`}>
                                          {assignment.status === 'pending_verification' ? 'pending' : assignment.status}
                                        </span>
                                        <span className="text-xs text-gray-600 whitespace-nowrap">{assignment.points_offered} pts</span>
                                        {isDraggable && (
                                          <div className="relative" ref={reassignDropdownId === assignment.id ? reassignDropdownRef : undefined}>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); setReassignDropdownId(reassignDropdownId === assignment.id ? null : assignment.id) }}
                                              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                                              title="Reassign chore"
                                            >
                                              <ArrowRightLeft className="w-3 h-3" />
                                            </button>
                                            {reassignDropdownId === assignment.id && (
                                              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[140px]">
                                                <p className="px-3 py-1 text-xs text-gray-500 font-medium">Reassign to:</p>
                                                {children.filter(c => c.id !== assignment.assigned_to).map(c => (
                                                  <button
                                                    key={c.id}
                                                    onClick={() => { onReassignChore?.(assignment.id, c.id); setReassignDropdownId(null) }}
                                                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition"
                                                  >
                                                    {c.display_name}
                                                  </button>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Custody Status */}
                {children.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Children Status</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {children.map(child => {
                        const isPresent = custodyData.get(child.display_name) || false
                        const childAssignments = dayAssignments.filter(a => a.assigned_to === child.id)
                        const CustodyIcon = isPresent ? Home : MapPin

                        return (
                          <div
                            key={child.id}
                            className={`p-3 rounded-lg border ${
                              isPresent ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <CustodyIcon className={`w-4 h-4 ${isPresent ? 'text-blue-600' : 'text-gray-400'}`} />
                              <span className={`font-medium ${isPresent ? 'text-blue-900' : 'text-gray-600'}`}>
                                {child.display_name}
                              </span>
                            </div>
                            <p className="text-xs text-gray-600">
                              {isPresent ? 'At home' : 'Away'}
                              {childAssignments.length > 0 && (
                                <> • {childAssignments.filter(a =>
                                  a.status === 'completed' || a.status === 'verified' || a.status === 'pending_verification'
                                ).length}/{childAssignments.length} chores</>
                              )}
                            </p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {dayEvents.length === 0 && dayAssignments.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No events or chores for this day</p>
                  </div>
                )}

                {/* Add Event Button - always visible */}
                <div className={`text-center ${dayEvents.length === 0 && dayAssignments.length === 0 ? '' : 'mt-4 pt-3 border-t border-gray-100'}`}>
                  <button
                    onClick={() => handleAddEvent(selectedDate)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    + Add an event
                  </button>
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {/* Add/Edit Event Modal */}
      <UnifiedAddEventModal
        isOpen={isAddEventModalOpen}
        onClose={() => {
          setIsAddEventModalOpen(false)
          setDuplicateEvent(null)
          setEditEvent(null)
        }}
        onSuccess={handleEventCreated}
        initialDate={addEventInitialDate}
        isAdmin={isFeatureEnabled('school_integration')}
        isKid={!!isKid}
        currentUserId={user?.id}
        duplicateEvent={duplicateEvent}
        editEvent={editEvent}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirmEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Event</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete "{deleteConfirmEvent.title}"? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmEvent(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteEvent(deleteConfirmEvent)}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors flex items-center gap-2"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
